<template>
  <DropDownSelect v-if="options.length || selected">
    <template #activator="{ open, showDropDown }">
      <button
        type="button"
        class="flex items-center gap-1 rounded-sm border border-solid p-2 text-body-s"
        :class="selected || showDropDown ? 'filter-selected' : 'filter-default'"
        :data-testid="`notification-center-filter-${level}-button`"
        @click.prevent="open"
      >
        {{ activeLabel }}
        <ChevronDownIcon class="lucide-icon-m" />
      </button>
    </template>
    <template #default="{ close }">
      <div class="flex w-80 items-center justify-between bg-surface-elevation-4 px-3 py-2 txt-body-s-bold">
        {{ t(`NotificationCenter.filters.${level}.headline`) }}
        <XIcon class="lucide-icon-s cursor-pointer" @click.prevent="close" />
      </div>
      <ul class="options-list m-0 w-80 list-none p-0">
        <li v-for="item in items" :key="item.key">
          <label
            class="peer block cursor-pointer has-[:focus-visible]:text-effect-focused has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-offset-2"
          >
            <input
              type="radio"
              :value="item.value ?? ''"
              :checked="selected === item.value"
              :name="`notification-${level}-option`"
              :data-testid="`notification-center-filter-${level}-option-${item.key}`"
              class="peer sr-only"
              @change="onChange(item.value)"
            />
            <span
              class="block px-3 py-2 peer-checked:hidden hover:bg-surface-elevation-2"
              :data-testid="`notification-center-filter-${level}-option-${item.key}-label`"
              >{{ item.text }}</span
            >
            <span class="hidden items-center px-3 py-2 peer-checked:flex hover:bg-surface-elevation-2">
              <span class="text-selected">{{ item.text }}</span>
              <CheckIcon class="ml-4 lucide-icon-s" />
            </span>
          </label>
        </li>
      </ul>
    </template>
  </DropDownSelect>
</template>

<script setup lang="ts">
import { ChevronDownIcon, XIcon, CheckIcon } from '@ramp106/omrjs-core-ui/icons'
import { track } from '@ramp106/omrjs-tracking-reviews'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

import { categoryPrefixFallbackLabel } from './notificationCategoryPrefix'

const props = defineProps<{ level: 'product' | 'topic'; options: string[]; selected: string | null }>()
const emit = defineEmits<{ select: [value: string | null] }>()

const { t, te } = useI18n()

const optionTranslationKey = (prefix: string) => prefix.replace(/\./g, '_')

const namesAnObjectPrototypeMember = (segment: string) => segment in Object.prototype

const spellsATranslationKey = (segment: string) => /^[a-z0-9_]+$/.test(segment)

const prefixes = computed(() => [...props.options, ...(props.selected && !props.options.includes(props.selected) ? [props.selected] : [])])

const ambiguousTranslationKeys = computed(() => {
  const counts = new Map<string, number>()
  prefixes.value.forEach((prefix) => {
    const segment = optionTranslationKey(prefix)
    counts.set(segment, (counts.get(segment) ?? 0) + 1)
  })
  return new Set([...counts].filter(([, count]) => count > 1).map(([segment]) => segment))
})

const optionLabel = (prefix: string) => {
  const segment = optionTranslationKey(prefix)
  const key = `NotificationCenter.filters.${props.level}.options.${segment}`
  const resolvable =
    spellsATranslationKey(segment) && !namesAnObjectPrototypeMember(segment) && !ambiguousTranslationKeys.value.has(segment)
  return resolvable && te(key) ? t(key) : categoryPrefixFallbackLabel(prefix)
}

const activeLabel = computed(() =>
  props.selected ? optionLabel(props.selected) : t(`NotificationCenter.filters.${props.level}.filtername`),
)

const items = computed<{ key: string; text: string; value: string | null }[]>(() => [
  { key: 'all', text: t(`NotificationCenter.filters.${props.level}.options.all`), value: null },
  ...prefixes.value.map((prefix) => ({ key: prefix, text: optionLabel(prefix), value: prefix })),
])

function onChange(value: string | null) {
  if (value === props.selected) return
  track('filter_apply', {
    surface: 'filter',
    filter_group: 'manage_notifications',
    filter_type: 'dropdown',
    filter_name: props.level,
    filter_value: value ?? 'all',
  })
  emit('select', value)
}
</script>

<style scoped>
@reference '@ramp106/omrjs-config-tailwind/styles/index.css';

.filter-selected {
  @apply border-strong text-subtle;
}
.filter-default {
  @apply border-main text-subtlest disabled:border-disabled disabled:text-disabled;
}
</style>
